import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, ImageIcon, ZoomIn } from "lucide-react";
import { Reveal, SectionLabel } from "../components/Motion";
import SEO from "../components/SEO";
import Lightbox from "../components/Lightbox";

const REALISATIONS = [
  {
    image:
      "https://customer-assets.emergentagent.com/job_chape-traditionnelle/artifacts/64dlk4nx_20260304_134950.jpg",
    type: "Chape traditionnelle",
    title: "Local industriel",
    location: "Alsace",
    surface: "3500",
  },
  {
    image:
      "https://customer-assets.emergentagent.com/job_chape-traditionnelle/artifacts/8scg9ah7_image_1777561239581.jpeg",
    type: "Béton dur",
    title: "Parking",
    location: "Suisse",
    surface: "≈ 2000",
  },
  {
    image:
      "https://customer-assets.emergentagent.com/job_chape-traditionnelle/artifacts/rofrxnm5_Snapchat-1794743808.jpg",
    type: "Béton dur",
    title: "Entrepôt",
    location: "Alsace",
    surface: "≈ 1500",
  },
  {
    image:
      "https://customer-assets.emergentagent.com/job_chape-traditionnelle/artifacts/nxyb2xbs_VID-20260501-WA0094.jpg",
    type: "Rampe d'accès garage",
    title: "Accès parking souterrain",
    location: "Suisse",
    surface: "≈ 60",
  },
];

export default function Realisations() {
  const { hash } = useLocation();
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    if (!hash) return;
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash]);

  const images = REALISATIONS.map((r) => r.image);

  return (
    <div data-testid="page-realisations">
      <SEO
        title="Réalisations chantiers — Chape & sols en Alsace"
        description="Découvrez nos réalisations : chapes, bétons, isolations et parquets sur des chantiers à Mulhouse, Illzach, Colmar et dans tout le Haut-Rhin. Photos avant/après."
        keywords="réalisations chape Mulhouse, chantiers chape Alsace, photos chape Illzach, projets Euro Chape"
        canonical="https://eurochape.com/realisations"
      />
      <section className="pt-32 md:pt-40 pb-20 px-6 md:px-12 lg:px-24 bg-[#EBEBE6] border-b border-[#D5D5D0]">
        <Reveal>
          <SectionLabel>Réalisations</SectionLabel>
          <h1 className="font-display font-black uppercase tracking-tighter mt-8 text-5xl sm:text-7xl lg:text-[7rem] leading-[0.88] text-[#1A2D7E]">
            Nos chantiers,
            <br />
            <span className="text-[#F08722]">notre signature.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-base sm:text-lg font-light leading-relaxed text-[#1A2D7E]">
            Une sélection de projets réalisés en Alsace, en Suisse et sur le
            Territoire de Belfort — maisons individuelles, rénovations, locaux
            professionnels et bâtiments industriels.
          </p>
        </Reveal>
      </section>

      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28">
        {REALISATIONS.length === 0 ? (
          <Reveal>
            <div className="border border-dashed border-[#D5D5D0] bg-white p-10 md:p-20 text-center">
              <div className="mx-auto w-16 h-16 bg-[#EBEBE6] flex items-center justify-center">
                <ImageIcon className="w-7 h-7 text-[#F08722]" strokeWidth={1.5} />
              </div>
              <h2 className="font-display font-bold uppercase tracking-tighter text-3xl md:text-4xl mt-8 text-[#1A2D7E]">
                Galerie en préparation
              </h2>
              <p className="mt-4 text-[#5C5E58] max-w-xl mx-auto">
                Nous mettons à jour cet espace avec nos derniers chantiers.
              </p>
            </div>
          </Reveal>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {REALISATIONS.map((r, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <button
                  type="button"
                  onClick={() => setLightboxIndex(i)}
                  data-testid={`realisation-${i}`}
                  className="group block w-full text-left border border-[#D5D5D0] bg-white overflow-hidden h-full flex-col cursor-zoom-in"
                  aria-label={`Agrandir ${r.title}`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-[#EBEBE6]">
                    <img
                      src={r.image}
                      alt={r.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute top-3 right-3 w-9 h-9 bg-[#1A2D7E] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <ZoomIn className="w-4 h-4" />
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="text-xs uppercase tracking-[0.18em] text-[#F08722] font-semibold">
                      {r.type}
                    </div>
                    <h3 className="font-display font-bold uppercase tracking-tight text-xl mt-2 text-[#1A2D7E]">
                      {r.title}
                    </h3>
                    <p className="text-sm text-[#5C5E58] mt-1">
                      {r.location} · {r.surface} m²
                    </p>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        )}
      </section>

      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-[#1A2D7E] text-white text-center">
        <Reveal>
          <h2 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-6xl lg:text-7xl leading-[0.9]">
            Votre projet,
            <br />
            <span className="text-[#F08722]">le prochain ici.</span>
          </h2>
          <Link
            to="/devis"
            className="mt-10 inline-flex items-center gap-3 bg-[#F08722] text-white px-10 py-5 text-xs uppercase tracking-[0.18em] font-semibold hover:bg-white hover:text-[#1A2D7E] transition-colors"
          >
            Demander un devis <ArrowUpRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </section>

      <Lightbox
        images={images}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onPrev={() => setLightboxIndex((i) => (i - 1 + images.length) % images.length)}
        onNext={() => setLightboxIndex((i) => (i + 1) % images.length)}
      />
    </div>
  );
}
